import React from 'react';
import { useNavigate } from 'react-router-dom';
import '../CSS/ExecCoaching.css';

const ExecCoaching: React.FC = () => {
  const navigate = useNavigate();
  const imagePath = `${process.env.PUBLIC_URL || ''}/assets/Web Version/Services/Executive Coaching for Academic Leaders/executive-coaching-for-academic-leaders-banner-image.png`;
  
  return (
    <div className="exec-coaching-page">
      <div className="exec-coaching-banner">
        <div 
          className="exec-coaching-hero-background"
          style={{ 
            backgroundImage: `url("${imagePath}")` 
          }}
        ></div>
        <div className="exec-coaching-hero-content">
          <p className="exec-coaching-breadcrumb">Our Services / Executive Coaching for Academic Leaders</p>
          <h1 className="exec-coaching-title">Executive Coaching for Academic Leaders</h1>
          <p className="exec-coaching-subtitle">Empowering Leaders to Navigate Change with Clarity, Confidence, and Purpose</p>
        </div>
      </div>

      <div className="exec-coaching-body">
        <div className="exec-coaching-intro">
          <p>
            At Myers McRae Executive Search and Consulting, we provide <strong>personalized executive coaching</strong> designed exclusively for academic leaders—including presidents, provosts, deans, department chairs, and senior administrators. Our program supports higher education leaders in strengthening their leadership effectiveness, strategic decision-making, and overall impact.
          </p>
          <p>
            As institutions navigate shifting student needs, financial pressures, and technological transformation, our coaching helps leaders balance scholarly values with the complex realities of organizational management. Through reflection, guidance, and practical tools, leaders gain clarity and confidence to lead with purpose.
          </p>
        </div>
      </div>

      <div className="exec-coaching-text-image-pair">
        <div className="exec-coaching-text-image-pair-container">
          <div className="exec-coaching-text-image-pair-image">
            <img 
              src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Services/Executive Coaching for Academic Leaders/personalized-coaching-section-image.png`}
              alt="Personalized Coaching"
            />
          </div>
          <div className="exec-coaching-text-image-pair-text">
            <h2 className="exec-coaching-text-image-pair-title">Personalized Coaching by Experienced Academic Leaders</h2>
            <p className="exec-coaching-text-image-pair-description">
              Our coaching is led by retired college and university presidents who bring firsthand knowledge of higher education leadership. Working one-on-one with each client, our coaches help identify strengths, uncover blind spots, and align leadership styles with institutional goals and values.
            </p>
            <p className="exec-coaching-text-image-pair-description">
              Through structured dialogue, constructive feedback, and focused goal-setting, participants gain insight into how their leadership behaviors influence institutional culture, team dynamics, and long-term outcomes.
            </p>
          </div>
        </div>
      </div>

      <div className="exec-coaching-body">
        <div className="exec-coaching-key-areas">
          <h2 className="exec-coaching-key-areas-title">Key Areas of Development</h2>
          <p className="exec-coaching-key-areas-description">
            Successful academic leadership requires the ability to collaborate, communicate, and build consensus in shared governance environments. Our coaches guide leaders in developing essential adaptive skills:
          </p>
          <div className="exec-coaching-key-areas-columns">
            <div className="exec-coaching-key-areas-column">
              <h3 className="exec-coaching-key-areas-column-title">1. Adaptive and Collaborative Leadership</h3>
              <ul className="exec-coaching-key-areas-list">
                <li>Enhanced communication and negotiation skills to navigate complex stakeholder relationships.</li>
                <li>Stronger emotional intelligence and self-awareness.</li>
                <li>The ability to lead effectively across diverse stakeholder groups—faculty, staff, trustees, and students.</li>
              </ul>
            </div>
            <div className="exec-coaching-key-areas-column">
              <h3 className="exec-coaching-key-areas-column-title">2. Strategic Thinking and Change Management</h3>
              <ul className="exec-coaching-key-areas-list">
                <li>Strengthening strategic and systems thinking capabilities.</li>
                <li>Expert guidance on managing institutional change and innovation.</li>
                <li>Fostering cultures of accountability and progress.</li>
                <li>Leading effectively during transitions or organizational restructuring.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="exec-coaching-divider">
        <img 
          src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Services/Executive Coaching for Academic Leaders/executive-coaching-for-academic-leaders-section-divider-image.png`}
          alt="Section Divider"
        />
      </div>

      <div className="exec-coaching-confidential">
        <div className="exec-coaching-confidential-container">
          <h2 className="exec-coaching-confidential-title">A Confidential and Reflective Process</h2>
          <p className="exec-coaching-confidential-description">
            Our coaching environment is <strong>confidential, supportive, and developmental</strong>—a trusted space where leaders can explore challenges such as conflict resolution, decision-making under pressure, and work-life balance. This reflective process builds <strong>resilience, clarity, and confidence</strong>, allowing leaders to sustain effectiveness over time.
          </p>
          
          <h3 className="exec-coaching-confidential-subtitle">Transformational Outcomes</h3>
          <p className="exec-coaching-confidential-intro">
            The Myers McRae Executive Coaching experience leads to:
          </p>
          
          <div className="exec-coaching-confidential-boxes">
            <div className="exec-coaching-confidential-box">
              <p>Measurable improvements in leadership performance</p>
            </div>
            <div className="exec-coaching-confidential-box">
              <p>Stronger institutional alignment and collaboration</p>
            </div>
            <div className="exec-coaching-confidential-box">
              <p>Enhanced emotional and strategic intelligence</p>
            </div>
            <div className="exec-coaching-confidential-box">
              <p>Greater personal and professional fulfillment</p>
            </div>
          </div>
          
          <p className="exec-coaching-confidential-conclusion">
            Ultimately, our coaching empowers higher education institutions to cultivate visionary, empathetic, and strategic leaders who guide their communities through change while upholding the academic mission and values that define higher education.
          </p>
        </div>
      </div>

      <div className="exec-coaching-contact">
        <h2 className="exec-coaching-contact-title">Partner with Myers McRae</h2>
        <p className="exec-coaching-contact-description">
          Whether you need to modernize your academic offerings across the board or launch a specialized, commercial-ready research center, Myers McRae provides the strategic insight and practical tools to secure your position as a leader in <strong>applied AI and innovation</strong>.
        </p>
        <p className="exec-coaching-contact-description">
          If your institution is looking to expand its leadership in applied research, STEM innovation, and industry collaboration, we invite you to explore an <strong>AI Imaging Lab pilot partnership</strong>.
        </p>
        <div className="exec-coaching-contact-buttons">
          <button 
            className="exec-coaching-contact-button exec-coaching-contact-button-primary"
            onClick={() => {
              navigate('/contact-us');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            Contact us to explore an AI Imaging Lab pilot partnership
          </button>
          <button 
            className="exec-coaching-contact-button exec-coaching-contact-button-secondary"
            onClick={() => {
              navigate('/contact-us');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            Contact us about our Academic Modernization approach
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExecCoaching;

